import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';
import { useAdmin } from '../../contexts/AdminContext';
import DriverForm from './DriverForm';
import './DriverManagement.css';

const DriverManagement = () => {
  const { admin } = useAdmin();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: '',
    city: '',
    status: ''
  });
  const [pagination, setPagination] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [cities, setCities] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  // Load drivers
  const loadDrivers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getDrivers(filters);
      setDrivers(response.drivers);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      setError('Taxifahrer yüklenirken hata oluştu');
      console.error('Load drivers error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load cities
  const loadCities = async () => {
    try {
      const citiesData = await adminApi.getCities();
      setCities(citiesData);
    } catch (err) {
      console.error('Load cities error:', err);
    }
  };

  useEffect(() => {
    loadDrivers();
  }, [filters]);

  useEffect(() => {
    loadCities();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleDeleteDriver = async (driverId) => {
    if (!confirm('Bu taxifahreri silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await adminApi.deleteDriver(driverId);
      loadDrivers(); // Refresh list
      alert('Taxifahrer başarıyla silindi');
    } catch (err) {
      alert('Taxifahrer silinirken hata oluştu');
      console.error('Delete driver error:', err);
    }
  };

  const handleCreateDriver = async (driverData) => {
    setFormLoading(true);
    try {
      await adminApi.createDriver(driverData);
      setShowCreateModal(false);
      loadDrivers(); // Refresh list
      alert('Taxifahrer başarıyla eklendi');
    } catch (err) {
      console.error('Create driver error:', err);
      throw err; // Re-throw to let DriverForm handle the error
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateDriver = async (driverData) => {
    setFormLoading(true);
    try {
      await adminApi.updateDriver(editingDriver.id, driverData);
      setEditingDriver(null);
      loadDrivers(); // Refresh list
      alert('Taxifahrer başarıyla güncellendi');
    } catch (err) {
      console.error('Update driver error:', err);
      throw err; // Re-throw to let DriverForm handle the error
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseCreateModal = () => {
    if (!formLoading) {
      setShowCreateModal(false);
    }
  };

  const handleCloseEditModal = () => {
    if (!formLoading) {
      setEditingDriver(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return '✅';
      case 'inactive': return '❌';
      case 'suspended': return '⚠️';
      default: return '❓';
    }
  };

  const getOnlineStatusIcon = (onlineStatus) => {
    switch (onlineStatus) {
      case 'online': return '🟢';
      case 'offline': return '⚪';
      case 'busy': return '🟡';
      default: return '⚫';
    }
  };

  return (
    <div className="driver-management">
      <div className="management-header">
        <div className="header-content">
          <h1>👥 Taxifahrer Yönetimi</h1>
          <p>Tüm taxifahrerleri görüntüleyin, düzenleyin ve yönetin</p>
        </div>
        <button 
          className="create-btn"
          onClick={() => setShowCreateModal(true)}
        >
          <span className="btn-icon">➕</span>
          Yeni Taxifahrer Ekle
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>🔍 Arama</label>
            <input
              type="text"
              placeholder="İsim, email veya telefon..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label>🌍 Şehir</label>
            <select
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
            >
              <option value="">Tüm şehirler</option>
              {cities.map(city => (
                <option key={city.city} value={city.city}>
                  {city.city} ({city.count})
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>📊 Durum</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Tüm durumlar</option>
              <option value="active">Aktif</option>
              <option value="inactive">İnaktif</option>
              <option value="suspended">Askıya alınmış</option>
            </select>
          </div>

          <div className="filter-group">
            <label>📄 Sayfa başına</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      {pagination && (
        <div className="results-summary">
          <span className="summary-text">
            {pagination.total_items} taxifahrerden {((pagination.current_page - 1) * pagination.items_per_page) + 1}-
            {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)} arası gösteriliyor
          </span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={loadDrivers} className="retry-btn">Tekrar Dene</button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Taxifahrer listesi yükleniyor...</p>
        </div>
      ) : (
        <>
          {/* Drivers Table */}
          <div className="drivers-table-container">
            <table className="drivers-table">
              <thead>
                <tr>
                  <th>👤 Taxifahrer</th>
                  <th>📧 İletişim</th>
                  <th>🌍 Şehir</th>
                  <th>📊 Durum</th>
                  <th>🔄 Online</th>
                  <th>📅 Kayıt Tarihi</th>
                  <th>⚙️ İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map(driver => (
                  <tr key={driver.id} className="driver-row">
                    <td className="driver-info">
                      <div className="driver-avatar">
                        <span className="avatar-text">
                          {driver.first_name?.charAt(0)}{driver.last_name?.charAt(0)}
                        </span>
                      </div>
                      <div className="driver-details">
                        <div className="driver-name">
                          {driver.first_name} {driver.last_name}
                        </div>
                        <div className="driver-id">ID: {driver.id}</div>
                      </div>
                    </td>
                    
                    <td className="contact-info">
                      <div className="email">{driver.email}</div>
                      <div className="phone">{driver.phone || 'Telefon yok'}</div>
                    </td>
                    
                    <td className="city-info">
                      <span className="city-tag">{driver.city}</span>
                    </td>
                    
                    <td className="status-info">
                      <span className={`status-badge status-${driver.status}`}>
                        {getStatusIcon(driver.status)} {driver.status}
                      </span>
                    </td>
                    
                    <td className="online-info">
                      <span className={`online-badge online-${driver.online_status || 'unknown'}`}>
                        {getOnlineStatusIcon(driver.online_status)} 
                        {driver.online_status || 'Bilinmiyor'}
                      </span>
                      {driver.last_seen && (
                        <div className="last-seen">
                          Son: {formatDate(driver.last_seen)}
                        </div>
                      )}
                    </td>
                    
                    <td className="date-info">
                      {formatDate(driver.created_at)}
                    </td>
                    
                    <td className="actions">
                      <div className="action-buttons">
                        <button
                          className="action-btn edit"
                          onClick={() => setEditingDriver(driver)}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        {admin?.role === 'SUPER_ADMIN' && (
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteDriver(driver.id)}
                            title="Sil"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {drivers.length === 0 && !loading && (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3>Taxifahrer bulunamadı</h3>
              <p>Arama kriterlerinize uygun taxifahrer bulunmuyor.</p>
              <button 
                className="clear-filters-btn"
                onClick={() => setFilters({
                  page: 1,
                  limit: 10,
                  search: '',
                  city: '',
                  status: ''
                })}
              >
                Filtreleri Temizle
              </button>
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
              >
                ⬅️ Önceki
              </button>
              
              <div className="pagination-info">
                {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === pagination.total_pages || 
                    Math.abs(page - pagination.current_page) <= 1
                  )
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] < page - 1 && (
                        <span className="pagination-ellipsis">...</span>
                      )}
                      <button
                        className={`pagination-number ${page === pagination.current_page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))
                }
              </div>
              
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.total_pages}
              >
                Sonraki ➡️
              </button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) handleCloseCreateModal();
        }}>
          <div className="modal-content driver-form-modal">
            <DriverForm
              driver={null}
              cities={cities}
              onSubmit={handleCreateDriver}
              onCancel={handleCloseCreateModal}
              isLoading={formLoading}
            />
          </div>
        </div>
      )}

      {editingDriver && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) handleCloseEditModal();
        }}>
          <div className="modal-content driver-form-modal">
            <DriverForm
              driver={editingDriver}
              cities={cities}
              onSubmit={handleUpdateDriver}
              onCancel={handleCloseEditModal}
              isLoading={formLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverManagement;
